import { PickemLeaderboardFilters } from "components/FilterPanels/PickemLeaderboardFilters";
import { PaginationSwitcher } from "components/Pagination/PaginationSwitcher";
import { useFetchUsernames } from "hooks/useFetchUsernames";
import { useTournamentPickemFilter } from "model/Filter/PickemFilters";
import { useYearFilter } from "model/Filter/TournamentFilters";
import {
  getLeaderboardUsersBySort,
  Leaderboard,
  leaderboardSortCached,
  leaderboardUidCached,
} from "model/Leaderboard/Leaderboard";
import { LeaderboardUser } from "model/Leaderboard/User/LeaderboardUser";
import { JSX, useEffect, useState } from "react";
import {
  selectFetchingLeaderboardUsers,
  selectLeaderboard,
} from "selectors/Leaderboard";
import { selectTournamentIdFromName } from "selectors/Tournament";
import { selectAllUsernamesFetched, selectUid } from "selectors/User";
import {
  fetchLeaderboardUsersBySort,
  fetchLeaderboardUsersByUid,
} from "app/slices/LeaderboardSlice";
import { SecondaryButton } from "styles/elements";
import { CenterFlexRow } from "styles/layout";
import { EmptyGrowPanelText, ErrorText } from "styles/text";
import { SegmentPickemLeaderboard } from "./SegmentPickemLeaderboard";
import { SwitcherContainer } from "./styles";
import {
  isSegmentLeaderboardUser,
  SegmentLeaderboardUser,
} from "model/Leaderboard/User/SegmentLeaderboardUser";
import { Panel } from "components/Panel/Panel";
import { useFilter } from "model/Filter/Filters";
import {
  isTournamentLeaderboardUser,
  TournamentLeaderboardUser,
} from "model/Leaderboard/User/TournamentLeaderboardUser";
import { TournamentPickemLeaderboard } from "./TournamentPickemLeaderboard";
import { getBeaconLeaderboardIdTemp } from "./GroupPickemLeaderboard";
import { useAppDispatch, useAppSelector } from "app/hooks";

const AMOUNT_PER_PAGE = 25;
export const GlobalPickemLeaderboard = () => {
  const dispatch = useAppDispatch();
  const uid = useAppSelector(selectUid);

  const yearFilter = useYearFilter(false);
  const selectedYear = yearFilter.selectedValue;
  const tournamentFilter = useTournamentPickemFilter(
    selectedYear,
    "Global Playoffs"
  );
  const selectedTournament = tournamentFilter.selectedValue;

  const segmentFilter = useFilter(
    selectedTournament?.includes("Beacon") ? ["All", "Div 1", "Div 2"] : ["All"]
  );

  const selectedSegment =
    segmentFilter.options.length > 1 ? segmentFilter.selectedValue : "";
  const isTournamentLeaderboard = selectedSegment === "All";

  const leaderboardId = useAppSelector((state) =>
    !!selectedSegment
      ? getBeaconLeaderboardIdTemp(selectedTournament, selectedSegment)
      : selectTournamentIdFromName(state, selectedTournament)
  );

  useEffect(() => {
    setSelectedPage(1);
  }, [selectedTournament]);

  const leaderboard = useAppSelector((state) =>
    selectLeaderboard(state, leaderboardId)
  );

  const maxPage = !!leaderboard ? getMaxPage(leaderboard) : 0;
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const minSort = (selectedPage - 1) * AMOUNT_PER_PAGE + 1;
  const maxSort = minSort + AMOUNT_PER_PAGE - 1;

  const sortsToFetch: number[] = [];
  for (var i = minSort; i <= maxSort; i++) {
    if (!!leaderboard && !leaderboardSortCached(leaderboard, i)) {
      sortsToFetch.push(i);
    }
  }

  const fetchingLbUsers = useAppSelector(selectFetchingLeaderboardUsers);

  useEffect(() => {
    if (
      !!leaderboard &&
      !!leaderboardId &&
      sortsToFetch.length > 0 &&
      fetchingLbUsers === "idle"
    ) {
      dispatch(
        fetchLeaderboardUsersBySort({
          leaderboardId,
          sortsToFetch,
        })
      );
    }
  }, [sortsToFetch, fetchingLbUsers]);

  useEffect(() => {
    if (
      !!leaderboard &&
      !!leaderboardId &&
      !!uid &&
      !leaderboardUidCached(leaderboard, uid) &&
      fetchingLbUsers === "idle"
    ) {
      dispatch(
        fetchLeaderboardUsersByUid({
          leaderboardId,
          uids: [uid],
        })
      );
    }
  }, [uid, fetchingLbUsers]);

  const leaderboardUsers =
    getLeaderboardUsersBySort(leaderboard, minSort, maxSort)?.filter(
      (l) => !!l
    ) ?? [];

  const loggedInLeaderboardUser = !!uid ? leaderboard?.users[uid] : undefined;
  const loggedInUsersPage = loggedInLeaderboardUser
    ? getLoggedInUsersPage(loggedInLeaderboardUser)
    : undefined;

  const uidsToFetch = leaderboardUsers.map((l) => l.uid);
  if (!!uid) {
    uidsToFetch.push(uid);
  }
  useFetchUsernames(uidsToFetch);
  const usernamesFetched = useAppSelector((state) =>
    selectAllUsernamesFetched(state, uidsToFetch)
  );

  const dataFetched = usernamesFetched && fetchingLbUsers === "idle";
  var content: JSX.Element;
  if (
    !leaderboardId ||
    !leaderboard ||
    maxPage === undefined ||
    leaderboard.count === 0
  ) {
    content = (
      <EmptyGrowPanelText>
        The leaderboard for the selected filters will appear once calculated
      </EmptyGrowPanelText>
    );
  } else if (fetchingLbUsers === "error") {
    content = (
      <CenterFlexRow>
        <ErrorText>Unexpected error, unable to load leaderboard</ErrorText>
      </CenterFlexRow>
    );
    // } else if (isCombinedLeaderboard) {
    //   const typedLoggedInLeaderboardUser =
    //     !!loggedInLeaderboardUser &&
    //     isCombinedLeaderboardUser(loggedInLeaderboardUser)
    //       ? (loggedInLeaderboardUser as CombinedLeaderboardUser)
    //       : undefined;
    //   content = (
    //     <>
    //       <CombinedPickemLeaderboard
    //         tournamentIds={tournamentIdsWithPickems}
    //         leaderboardUsers={
    //           leaderboardUsers.filter((u) =>
    //             isCombinedLeaderboardUser(u)
    //           ) as CombinedLeaderboardUser[]
    //         }
    //         loggedInLeaderboardUser={typedLoggedInLeaderboardUser}
    //         paginationVisible={maxPage > 1}
    //       />
    //       {getSwitcher(selectedPage, setSelectedPage, maxPage, loggedInUsersPage)}
    //     </>
    //   );
  } else if (isTournamentLeaderboard) {
    const typedLoggedInLeaderboardUser =
      !!loggedInLeaderboardUser &&
      isTournamentLeaderboardUser(loggedInLeaderboardUser)
        ? (loggedInLeaderboardUser as TournamentLeaderboardUser)
        : undefined;
    content = (
      <>
        <TournamentPickemLeaderboard
          tournamentId={leaderboardId}
          leaderboardUsers={
            leaderboardUsers.filter((u) =>
              isTournamentLeaderboardUser(u)
            ) as TournamentLeaderboardUser[]
          }
          loggedInLeaderboardUser={typedLoggedInLeaderboardUser}
          paginationVisible={maxPage > 1}
        />
        {getSwitcher(selectedPage, setSelectedPage, maxPage, loggedInUsersPage)}
      </>
    );
  } else {
    const typedLoggedInLeaderboardUser =
      !!loggedInLeaderboardUser &&
      isSegmentLeaderboardUser(loggedInLeaderboardUser)
        ? (loggedInLeaderboardUser as SegmentLeaderboardUser)
        : undefined;
    content = (
      <>
        <SegmentPickemLeaderboard
          tournamentId={leaderboardId}
          leaderboardUsers={
            leaderboardUsers.filter((u) =>
              isSegmentLeaderboardUser(u)
            ) as SegmentLeaderboardUser[]
          }
          loggedInLeaderboardUser={typedLoggedInLeaderboardUser}
          paginationVisible={maxPage > 1}
        />
        {getSwitcher(selectedPage, setSelectedPage, maxPage, loggedInUsersPage)}
      </>
    );
  }

  return (
    <>
      <PickemLeaderboardFilters
        yearFilter={yearFilter}
        tournamentFilter={tournamentFilter}
        segmentFilter={!!selectedSegment ? segmentFilter : undefined}
        header="Leaderboard"
      />
      <Panel loading={!dataFetched}>{content}</Panel>
    </>
  );
};

const getMaxPage = (leaderboard: Leaderboard) => {
  if (leaderboard.count === 0) {
    return undefined;
  }

  return Math.ceil(leaderboard.count / AMOUNT_PER_PAGE);
};

const getLoggedInUsersPage = (user: LeaderboardUser) => {
  if (user.sort === 0) {
    return undefined;
  }

  return Math.ceil(user.sort / AMOUNT_PER_PAGE);
};

const getSwitcher = (
  selectedPage: number,
  setSelectedPage: React.Dispatch<React.SetStateAction<number>>,
  maxPage: number,
  loggedInUsersPage: number | undefined
) => {
  if (maxPage < 2) {
    return <></>;
  }

  return (
    <SwitcherContainer>
      <PaginationSwitcher
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        maxPage={maxPage}
      />
      <CenterFlexRow style={{ marginTop: "2px" }}>
        <SecondaryButton
          hidden={!loggedInUsersPage}
          onClick={() => setSelectedPage(loggedInUsersPage ?? 1)}
        >
          Jump to self
        </SecondaryButton>
      </CenterFlexRow>
    </SwitcherContainer>
  );
};
